import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import CustomLink from '@gc-npm/components.custom-link';
import TextInfo, {
  TEXT_ALIGN,
  TEXT_COLOR,
  TEXT_KIND,
  TEXT_SIZE,
  TEXT_FONT_WEIGHT,
  TEXT_DISPLAY,
  TEXT_HEADING_LEVEL,
} from '@gc-npm/components.text';
import { CARDS_WIDTH, TIPOS_R2R, DEVICE_SLIDER_INFO } from '@/utils/constants';
import Carousel from '@gc-npm/components.carousel';
import DeviceCard from '@/components/DevicesSlider/DeviceCard';

const COMPONENT_NAME = 'devicesSlider';

const DevicesSlider = ({ fakeSection }) => {
  const sliderRef = useRef(null);
  const userType = useSelector((state) => state.main?.context?.userType);
  const isClient = userType === 'Telco';

  const devicesStore = useSelector((state) => state.devices?.devices);
  const r2rType = useSelector((state) => state.client?.r2rType);

  const isR2rAditional = r2rType === TIPOS_R2R.ADICIONAL;

  const taggingInfo = {
    componentContent: '',
    componentName: COMPONENT_NAME,
    componentProduct: '',
    componentVariation: 'All',
  };
  const devices = isR2rAditional
    ? devicesStore?.[TIPOS_R2R.ADICIONAL]
    : devicesStore?.[TIPOS_R2R.INCLUIDO];

  const deviceSliderInfoKey = `${isClient ? 'client' : 'nonClient'}-${isR2rAditional ? 'additional' : 'included'}`;
  const {
    title,
    subtitle,
    linkText,
    link,
  } = DEVICE_SLIDER_INFO[deviceSliderInfoKey];

  useEffect(() => {
    const carouselWindow = sliderRef.current?.querySelector('.carousel-window');
    if (carouselWindow) {
      carouselWindow.setAttribute('tabindex', '0');
    }
  }, [devices]);

  if (!devicesStore) {
    return null; // or handle the case where devicesStore is not available }
  }

  return (
    <div className="DevicesSlider" ref={sliderRef}>
      <div className="DevicesSlider_info">
        <div className="DevicesSlider_info-title">
          {title && (
            <TextInfo
              text={title}
              kind={TEXT_KIND.HEADING}
              level={TEXT_HEADING_LEVEL.H2}
              color={TEXT_COLOR.MOVISTAR_BLACK}
              size={TEXT_SIZE.T5}
              align={TEXT_ALIGN.LEFT}
              fontType={TEXT_FONT_WEIGHT.BOLD}
            />
          )}
        </div>
        <div className="DevicesSlider_info-subtitle">
          {subtitle && (
            <TextInfo
              text={subtitle}
              color={TEXT_COLOR.GREY6}
              kind={TEXT_KIND.HEADING}
              level={TEXT_HEADING_LEVEL.H3}
              size={TEXT_SIZE.SUBTITLE2}
              align={TEXT_ALIGN.LEFT}
              fontType={TEXT_FONT_WEIGHT.REGULAR}
              display={TEXT_DISPLAY.INLINE}
              className="DevicesSlider_info-subtitle__linkText"
            />
          )}
          {linkText && link && (
            <CustomLink
              ariaLabel={linkText}
              taggingInfo={{ ...taggingInfo, componentContent: linkText }}
              href={link}
              target="_blank"
            >
              <TextInfo
                text={linkText}
                kind={TEXT_KIND.SPAN}
                color={TEXT_COLOR.MOVISTAR_BLUE}
                size={TEXT_SIZE.SUBTITLE2}
                align={TEXT_ALIGN.LEFT}
                fontType={TEXT_FONT_WEIGHT.REGULAR}
                display={TEXT_DISPLAY.INLINE}
              />
            </CustomLink>
          )}
        </div>
      </div>
      <div className="DevicesSlider_content">
        <div className="devices">
          {fakeSection && (
            <Carousel
              slideSeparation={8}
              breakpoint={768}
              maxPointNum={0}
              className="fakeCarrousel"
            >
              {Array.from({ length: 4 }, (v, i) => (
                <div key={`fakeDevice-${i}`} className="fakeDevice skeleton" />
              ))}
            </Carousel>
          )}
          {!fakeSection && devices && devices.length > 0 && (
            <Carousel
              slideSeparation={8}
              breakpoint={768}
              maxPointNum={0}
              className="DevicesSlider_content-carousel"
            >
              {devices.map((eachDevice) => {
                return (
                  <DeviceCard
                    device={eachDevice}
                    isR2rAditional={isR2rAditional}
                    hasStamps={devices.some((device) => device.stamp)}
                  />
                )
              })}
            </Carousel>
          )}
        </div>
      </div>

      <style jsx>
        {`
          .DevicesSlider {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            &_info {
              display: flex;
              flex-direction: column;
              gap: 1rem;
            }
            &_info-subtitle {
              :global(.DevicesSlider_info-subtitle__linkText) {
                padding-right: 4px;
              }
            }
            &_content {
              display: flex;
              flex-direction: row;
              flex-wrap: nowrap;
              width: 100%;
              .devices {
                width: 100%;
                .fakeDevice {
                  display: flex;
                  flex-direction: column;
                  width: ${CARDS_WIDTH};
                  min-width: 250px;
                  height: 120px;
                  border: 1px solid #eeeeee;
                  border-radius: 1rem;
                  overflow: hidden;
                }
              }
            }
          }
        `}
      </style>
    </div>
  );
};
export default DevicesSlider;
